import {
  WindowWithUcCmp,
  UcCmp,
  ServiceInfoItem,
  WindowWithUserlike,
  LastSavedService,
} from "./model/usercentrics/usercentricsTypes";

export class UsercentricsController {
  private serviceInfo = {
    YouTubeVideo: {
      id: "BJz7qNsdj-7",
      name: "youtube-video",
      handlerLoading: {
        selector: "iframe",
        deactivatedAttr: "data-src",
        activatedAttr: "src",
      },
      handlerViewSelector: "",
      customWaitingFunc: false,
    },
    Userlike: {
      id: "xyKq_EfQf",
      name: "Userlike",
      handlerLoading: {
        selector: "",
        deactivatedAttr: "data-src",
        activatedAttr: "src",
      },
      handlerViewSelector: "",
      customWaitingFunc: this.userlikeCustomWaiting,
    },
  };

  private attributeNames = {
    serviceName: "data-uc-type",
    activateButton: "data-uc-activate-service",
    showConsentDetails: "data-uc-show-consent-details",
    viewController: "data-uc-is-allowed",
    isLoading: "data-uc-is-loading",
  };

  private readonly USERCENTRICS_ACTIONS = {
    ACCEPT_ALL: "ACCEPT_ALL",
    DENY_ALL: "DENY_ALL",
    SAVE: "SAVE",
  };

  private usercentricsController: null | UcCmp;
  private timeout: number = 8000;
  private lastSaved: LastSavedService | null = null;

  constructor() {
    if (!this.usercentricsExist()) {
      window.addEventListener(
        "UC_UI_INITIALIZED",
        this.initialization.bind(this),
      );
    } else {
      this.initialization().then(() => {
        /*ignore*/
      });
    }
  }

  private async initialization() {
    if (this.usercentricsExist()) {
      const windowWithUcCmp = window as WindowWithUcCmp;
      this.usercentricsController = windowWithUcCmp.__ucCmp as UcCmp;

      await this.scanDom();

      this.registerUsercentricsEventListener();
      this.registerButtonEventListeners();
    } else {
      this.usercentricsController = null;
    }
  }

  private registerUsercentricsEventListener(): void {
    window.addEventListener("UC_UI_CMP_EVENT", (event) => {
      const customEvent = event as CustomEvent;
      if (!customEvent?.detail) return;

      if (
        Object.values(this.USERCENTRICS_ACTIONS).includes(
          customEvent.detail.type,
        )
      ) {
        this.scanDom().then(() => {
          /*ignore*/
        });
      }
    });
  }

  private registerButtonEventListeners(): void {
    const activateButtons = document.querySelectorAll(
      `[${this.attributeNames.activateButton}]`,
    );

    const consentDetailButtons = document.querySelectorAll(
      `[${this.attributeNames.showConsentDetails}]`,
    );

    if (activateButtons.length !== 0) {
      activateButtons.forEach((button) =>
        button.addEventListener(
          "click",
          this.onActivateServiceHandler.bind(this),
        ),
      );
    }

    if (consentDetailButtons.length !== 0) {
      consentDetailButtons.forEach((button) =>
        button.addEventListener("click", this.onShowConsentDetails.bind(this)),
      );
    }
  }

  private async scanDom() {
    const unsortedServices = Array.from<HTMLElement>(
      document.querySelectorAll(`[${this.attributeNames.serviceName}]`),
    ).filter((entry) => entry.tagName.toLowerCase() !== "script");
    if (unsortedServices.length === 0) return;

    const serviceOperations = unsortedServices.map(async (element) => {
      const serviceName = element?.getAttribute(
        this.attributeNames.serviceName,
      );
      if (!serviceName) return;

      const serviceId = await this.getServiceIdByName(serviceName);
      if (!serviceId) return;

      const serviceDetail = await this.getServiceDetailById(serviceId);
      if (!serviceDetail) return;

      const serviceInfo = this.getServiceInfoById(serviceId);
      if (!serviceInfo) return;

      if (serviceDetail?.consent?.given) {
        await this.serviceHasConsent(serviceInfo, element);
      } else {
        this.serviceHasNoConsent(serviceInfo, element);
      }
    });

    await Promise.all(serviceOperations);
    this.lastSaved = null;
  }

  private async serviceHasConsent(
    serviceInfo: ServiceInfoItem,
    rootElement: HTMLElement,
  ) {
    const viewTarget = this.getViewTarget(
      rootElement,
      serviceInfo.handlerViewSelector,
    );

    if (
      viewTarget &&
      viewTarget.getAttribute(this.attributeNames.viewController) === "false"
    ) {
      this.updateTargetSource(rootElement, serviceInfo, [
        serviceInfo.handlerLoading.deactivatedAttr,
        serviceInfo.handlerLoading.activatedAttr,
      ]);

      if (
        this.lastSaved?.serviceName === serviceInfo.name &&
        this.lastSaved?.consent === true
      ) {
        await this.awaitServiceReadiness(serviceInfo, viewTarget);
      }

      this.updateServiceView(viewTarget, true);
    }
  }

  private serviceHasNoConsent(
    serviceInfo: ServiceInfoItem,
    rootElement: HTMLElement,
  ) {
    const viewTarget = this.getViewTarget(
      rootElement,
      serviceInfo.handlerViewSelector,
    );
    if (
      viewTarget &&
      viewTarget.getAttribute(this.attributeNames.viewController) === "true"
    ) {
      this.updateTargetSource(rootElement, serviceInfo, [
        serviceInfo.handlerLoading.activatedAttr,
        serviceInfo.handlerLoading.deactivatedAttr,
      ]);
      this.updateServiceView(viewTarget, false);
    }
  }

  /*
   *
   * Event Handler
   *
   * */

  private async onShowConsentDetails(event: Event) {
    const trigger = event.currentTarget;
    if (!this.isHTMLElement(trigger)) return;

    const serviceName = trigger.getAttribute(
      this.attributeNames.showConsentDetails,
    );
    if (!serviceName) return;

    const serviceId = (await this.getServiceIdByName(serviceName)) || "";
    if (!serviceId) return;

    await this.showServiceDetails(serviceId);
  }

  private async onActivateServiceHandler(event: Event): Promise<void> {
    const trigger = event.currentTarget;
    if (!this.isHTMLElement(trigger)) return;

    const serviceName = trigger.getAttribute(
      this.attributeNames.activateButton,
    );

    if (!serviceName) return;
    await this.updateServiceByName(serviceName, true);
  }

  /*
   *
   * Getter FUnctions
   *
   * */

  private async getServiceDetailById(serviceId: string) {
    if (!this.usercentricsController) return;
    const details = await this.usercentricsController?.getConsentDetails();
    return details?.services[serviceId] || undefined;
  }

  private async getServiceIdByName(serviceName: string) {
    if (!serviceName) return;
    return Object.values(this.serviceInfo).find(
      (serviceInfo) => serviceInfo.name === serviceName,
    )?.id;
  }

  private getServiceInfoById(serviceId: string): ServiceInfoItem | undefined {
    return Object.values(this.serviceInfo).find(
      (srvInfo) => srvInfo.id === serviceId,
    );
  }

  private getViewTarget(
    rootElement: HTMLElement,
    handlerView?: string,
  ): HTMLElement {
    const target = handlerView ? rootElement.querySelector(handlerView) : null;
    return this.isHTMLElement(target) ? target : rootElement;
  }

  /*
   *
   * Update Functions
   *
   * */

  private updateTargetSource(
    element: HTMLElement,
    serviceInfo: ServiceInfoItem,
    attributes: string[],
  ) {
    if (
      !serviceInfo.handlerLoading.selector ||
      !element ||
      !serviceInfo ||
      !attributes
    )
      return;

    const targetElement = element.querySelector(
      serviceInfo.handlerLoading.selector,
    );

    if (!targetElement) return;

    const [fromAttr, toAttr] = attributes;
    if (!fromAttr || !toAttr) return;

    const oldValue = targetElement.getAttribute(toAttr) || "";
    const newValue = targetElement.getAttribute(fromAttr) || "";

    targetElement.setAttribute(fromAttr, oldValue);
    targetElement.setAttribute(toAttr, newValue);
  }

  private async updateServiceByName(serviceName: string, consent: boolean) {
    const id = await this.getServiceIdByName(serviceName);
    if (!id || !this.usercentricsController) return;
    await this.usercentricsController?.updateServicesConsents([
      { id, consent },
    ]);
    this.lastSaved = { serviceName, consent };
    await this.usercentricsController.saveConsents();
  }

  private updateServiceView(element: HTMLElement, allowed: boolean) {
    if (!element) return;
    const stringStatus = allowed ? "true" : "false";
    element.setAttribute(this.attributeNames.viewController, stringStatus);
  }

  private updateLoadingState(viewTarget: HTMLElement, status: boolean) {
    const stringStatus = status ? "true" : "false";
    viewTarget.setAttribute(this.attributeNames.isLoading, stringStatus);
  }

  /*
   *
   * Helper Functions
   *
   * */

  private async showServiceDetails(serviceId: string) {
    if (!this.usercentricsController) return;
    await this.usercentricsController.showServiceDetails(serviceId);
  }

  private timeoutPromise(signal: AbortSignal) {
    return new Promise<boolean>((_, reject) => {
      const timer = setTimeout(() => {
        reject(new Error("Timeout"));
      }, this.timeout);

      if (signal) {
        signal.addEventListener("abort", () => {
          clearTimeout(timer);
        });
      }
    });
  }

  private async awaitServiceReadiness(
    serviceInfo: ServiceInfoItem,
    viewTarget: HTMLElement,
  ): Promise<boolean> {
    if (!serviceInfo) return false;
    const { name, customWaitingFunc } = serviceInfo;

    const controller = new AbortController();
    const { signal } = controller;

    if (typeof customWaitingFunc === "function") {
      try {
        this.updateLoadingState(viewTarget, true);

        await Promise.race([customWaitingFunc(), this.timeoutPromise(signal)]);
        controller.abort();

        this.updateLoadingState(viewTarget, false);
      } catch (error) {
        console.error(`Error while waiting for the service ${name}:`, error);
        return true;
      }
    }
    return true;
  }

  private isHTMLElement(element: unknown): element is HTMLElement {
    return element instanceof HTMLElement;
  }

  /*Custom Waiting Functions*/
  private async userlikeCustomWaiting(
    timeout = 10000,
    interval = 100,
  ): Promise<boolean> {
    return new Promise((resolve, reject) => {
      const start = Date.now();
      const check = () => {
        const userlikeWindow = window as unknown as WindowWithUserlike;
        if (typeof userlikeWindow.userlike === "object") {
          resolve(true);
        } else if (Date.now() - start > timeout) {
          reject(new Error("Userlike konnte nicht geladen werden."));
        } else {
          setTimeout(check, interval);
        }
      };
      check();
    });
  }

  private usercentricsExist(): boolean {
    const windowWithUcCmp = window as WindowWithUcCmp;
    return windowWithUcCmp?.__ucCmp?.ready === true;
  }
}

new UsercentricsController();
